import type {
  MinervaDataRequest,
  MinervaMetricInput,
} from '@backoffice-frontend/graphql'
import type {
  BinaryOperator,
  CubeApi,
  Filter,
  Meta,
  Query,
  UnaryOperator,
} from '@cubejs-client/core'
import type { GlobalCubeFilters } from '../components/CubeContext'
import type { Timeframe } from '../components/TimeframeSelect'
import { getDatesForTimeframe } from './getDatesForTimeframe'
import { mergeCubeFilters } from './mergeCubeFilters'

type MinervaCatalogMember = {
  name: string
  type?: string
  /** Only when it says more than the name. */
  title?: string
  format?: string
  description?: string
}

/**
 * The cubes the model may ask data from, sent with a new chat: every cube
 * Cube's `/meta` returns for the user, which is already limited to what they
 * may query. Members are bare names: the model writes them bare
 * (`prompts/chat.md`), and `toCubeQuery` adds the cube back.
 */
export type MinervaCatalog = {
  cubes: Array<{
    name: string
    title: string
    /** A cube of the view's metrics, as opposed to one the view doesn't show. */
    inView: boolean
    /** Null for a cube without a time dimension. */
    timeDimension: string | null
    measures: MinervaCatalogMember[]
    dimensions: MinervaCatalogMember[]
  }>
}

/** The cubes of the view's metrics, marked `inView` in the catalog. */
export const getMinervaMetricCubes = (
  metrics: MinervaMetricInput[] | undefined,
): string[] => [
  ...new Set((metrics ?? []).map(metric => metric.cube).filter(Boolean)),
]

/** `prompts/chat.md` allows 3 queries, but the backend doesn't enforce it. */
const MAX_QUERIES = 3

/** The backend rejects a larger `dataResult` (`METRICS_MAX_BYTES`). */
const MAX_DATA_RESULT_BYTES = 256 * 1024

/** The granularity the model may ask for, from `prompts/chat.md`. */
const GRANULARITIES = ['hour', 'day', 'week', 'month'] as const

const isGranularity = (
  value: string,
): value is (typeof GRANULARITIES)[number] =>
  (GRANULARITIES as readonly string[]).includes(value)

/** Cube's binary and unary filter operators. Others are dropped. */
const OPERATORS = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'startsWith',
  'endsWith',
  'gt',
  'gte',
  'lt',
  'lte',
  'inDateRange',
  'notInDateRange',
  'beforeDate',
  'afterDate',
  'set',
  'notSet',
] as const satisfies readonly (BinaryOperator | UnaryOperator)[]

const isOperator = (value: string): value is (typeof OPERATORS)[number] =>
  (OPERATORS as readonly string[]).includes(value)

/** One model filter as a Cube filter, or none for an unknown operator. */
const toCubeFilter = (
  filter: NonNullable<ModelQuery['filters']>[number],
  prefix: (member: string) => string,
): Filter[] => {
  const { operator } = filter
  if (!isOperator(operator)) return []
  const member = prefix(filter.member)
  return operator === 'set' || operator === 'notSet'
    ? [{ member, operator }]
    : [{ member, operator, values: filter.values }]
}

/** One query as the model writes it (`prompts/chat.md`). */
type ModelQuery = {
  cube: string
  measures: string[]
  dimensions?: string[]
  timeDimension?: {
    dimension: string
    granularity: string
    dateRange?: [string, string]
  }
  filters?: Array<{ member: string; operator: string; values: string[] }>
  order?: Array<[string, string]>
  limit?: number
}

/** The backend rejects a larger catalog (`METRICS_MAX_BYTES`). */
const MAX_CATALOG_BYTES = 256 * 1024

const byteLength = (text: string) => new TextEncoder().encode(text).length

const normalize = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, '')

type MetaMember = {
  name: string
  type?: string
  title?: string
  shortTitle?: string
  /** A dimension's can be an object; only a measure's `percent` / `currency`. */
  format?: unknown
  description?: string
}

const toCatalogMember = (member: MetaMember): MinervaCatalogMember => {
  const name = member.name.split('.').slice(1).join('.')
  const title = member.shortTitle ?? member.title
  return {
    name,
    ...(member.type ? { type: member.type } : {}),
    // Cube's titles mostly repeat the name; the few that don't carry units.
    ...(title && normalize(title) !== normalize(name) ? { title } : {}),
    ...(typeof member.format === 'string' ? { format: member.format } : {}),
    ...(member.description ? { description: member.description } : {}),
  }
}

type CubeMetas = Pick<Meta, 'cubes'>

/** One tenant's cubes in one list. A cube named twice keeps its first entry. */
export const mergeMetas = (metas: CubeMetas[]): CubeMetas => {
  const cubes = new Map<string, CubeMetas['cubes'][number]>()
  for (const cube of metas.flatMap(meta => meta.cubes)) {
    if (!cubes.has(cube.name)) cubes.set(cube.name, cube)
  }
  return { cubes: [...cubes.values()] }
}

const NO_CUBES: CubeMetas = { cubes: [] }

/**
 * The `/meta` of each client, in order. The view's own tenant must answer; an
 * override tenant that doesn't leaves its cubes out instead of failing the chat.
 */
export const loadMetas = (cubeApis: CubeApi[]): Promise<CubeMetas[]> =>
  Promise.all(
    cubeApis.map((cubeApi, index) =>
      index === 0 ? cubeApi.meta() : cubeApi.meta().catch(() => NO_CUBES),
    ),
  )

// The time dimension keeps its cube prefix, as in the prompt's example.
// `toCubeQuery` accepts both spellings.
export const buildMinervaCatalog = (
  meta: CubeMetas,
  inViewCubeNames: string[],
): MinervaCatalog => ({
  cubes: meta.cubes.map(cube => ({
    name: cube.name,
    title: cube.title,
    inView: inViewCubeNames.includes(cube.name),
    timeDimension:
      cube.dimensions.find(dimension => dimension.type === 'time')?.name ??
      null,
    measures: cube.measures.map(toCatalogMember),
    dimensions: cube.dimensions.map(toCatalogMember),
  })),
})

/**
 * The catalog as JSON. Over the backend's limit the whole chat would fail, so
 * it falls back to the view's own cubes.
 */
export const stringifyMinervaCatalog = (catalog: MinervaCatalog): string => {
  const full = JSON.stringify(catalog)
  if (byteLength(full) <= MAX_CATALOG_BYTES) return full
  return JSON.stringify({ cubes: catalog.cubes.filter(cube => cube.inView) })
}

/**
 * The scope a data-request query runs in, the same as the view's tiles.
 * `globalFilters` are fixed when the chat opens. `defaultDateRange` is the
 * view's timeframe, used when the model names no valid range. `tileFilters`
 * are the filters a tile puts on its own cube, keyed by cube name.
 */
export type MinervaQueryScope = {
  serviceAreaUuid?: string
  globalFilters?: GlobalCubeFilters
  defaultDateRange?: [string, string]
  tileFilters?: Record<string, Filter[]>
}

/**
 * Turns one model query into a cubejs `Query`: adds the cube to bare
 * members, and the view's filters before the model's, as the tiles order
 * them. A date range the model names is kept unless it doesn't parse.
 */
export const toCubeQuery = (
  request: ModelQuery,
  {
    serviceAreaUuid,
    globalFilters,
    defaultDateRange,
  }: Omit<MinervaQueryScope, 'allowedCubes'>,
): Query => {
  const cube = request.cube
  const prefix = (member: string) =>
    member.includes('.') ? member : `${cube}.${member}`

  const modelRange = request.timeDimension?.dateRange
  const parseableRange: [string, string] | undefined =
    modelRange?.length === 2 &&
    modelRange.every(d => typeof d === 'string' && !Number.isNaN(Date.parse(d)))
      ? [modelRange[0], modelRange[1]]
      : undefined

  return {
    measures: (request.measures ?? []).map(prefix),
    dimensions: request.dimensions?.map(prefix),
    timeDimensions: request.timeDimension
      ? [
          {
            dimension: prefix(request.timeDimension.dimension),
            granularity: isGranularity(request.timeDimension.granularity)
              ? request.timeDimension.granularity
              : undefined,
            dateRange: parseableRange ?? defaultDateRange,
          },
        ]
      : [],
    filters: mergeCubeFilters({
      serviceAreaId: serviceAreaUuid,
      serviceAreaMember: `${cube}.service_area_uuid`,
      globalFilters,
      cubeForGlobalFilters: cube,
      localFilters: (request.filters ?? []).flatMap(filter =>
        toCubeFilter(filter, prefix),
      ),
    }),
    order: request.order?.reduce<Query['order']>((acc, [member, direction]) => {
      const key = prefix(member)
      return { ...acc, [key]: direction === 'asc' ? 'asc' : 'desc' }
    }, undefined),
    // No `rowLimit`: the DAP multi-tenant API rejects unknown query keys
    // ("rowLimit is not allowed") and cube's own default cap applies anyway.
  }
}

/** Unknown timeframes fall back to the dashboards' default, `this-week`. */
export const datesForViewTimeframe = (
  timeframe: string | undefined,
): [string, string] | undefined => {
  const dates =
    getDatesForTimeframe(timeframe as Timeframe) ??
    getDatesForTimeframe('this-week')
  return [dates.start.toISOString(), dates.end.toISOString()]
}

/** Why a query didn't run, sent to the model in place of its rows. */
type SkipReason = 'cube not in catalog' | 'cube not scoped to the view'

/**
 * Runs the model's queries with the user's own Cube session and returns the
 * `dataResult` JSON.
 *
 * The backend doesn't check the queries, so the scope is enforced here: a
 * query runs only on a cube of the user's Cube `/meta` (the catalog's), and
 * only if the view's service area or global filters reach that cube. A cube
 * the view doesn't render gets the view's fleet and region filters when it
 * has those dimensions. A view without any scope runs unfiltered, like its
 * tiles.
 *
 * `cubeApis` starts with the view's own tenant. A cube runs on the first
 * client whose `/meta` lists it, so a cube of an override tenant runs on that
 * tenant's client.
 */
export const runMinervaDataQueries = async (
  queries: unknown,
  cubeApis: CubeApi[],
  { serviceAreaUuid, tileFilters = {}, ...scope }: MinervaQueryScope,
): Promise<string> => {
  const modelQueries = parseModelQueries(queries).slice(0, MAX_QUERIES)
  const metas = await loadMetas(cubeApis)
  const clientByCube = new Map<string, CubeApi>()
  cubeApis.forEach((cubeApi, index) => {
    for (const cube of metas[index]?.cubes ?? []) {
      if (!clientByCube.has(cube.name)) clientByCube.set(cube.name, cubeApi)
    }
  })
  const meta = mergeMetas(metas)
  const catalogDimensions = new Set(
    meta.cubes.flatMap(cube => cube.dimensions.map(({ name }) => name)),
  )
  const cubesWithServiceArea = serviceAreaUuid
    ? getCubesWithServiceArea(meta)
    : new Set<string>()
  const viewIsScoped =
    Boolean(serviceAreaUuid) || hasAnyFilter(scope.globalFilters)

  const skipReason = (
    cube: string,
    carriedScope: Filter[] | null,
  ): SkipReason | null => {
    if (!clientByCube.has(cube)) return 'cube not in catalog'
    if (!viewIsScoped) return null
    const reachesCube =
      cubesWithServiceArea.has(cube) ||
      hasGlobalFilterFor(scope.globalFilters, cube) ||
      carriedScope !== null
    return reachesCube ? null : 'cube not scoped to the view'
  }

  const results = await Promise.all(
    modelQueries.map(async modelQuery => {
      const { cube } = modelQuery
      // The view's filters on this cube, else carried over from the view, else
      // the filters of the tile that queries it.
      const carriedScope = hasGlobalFilterFor(scope.globalFilters, cube)
        ? null
        : (carryViewScope(scope.globalFilters, cube, catalogDimensions) ??
          tileFilters[cube] ??
          null)
      const skipped = skipReason(cube, carriedScope)
      const cubeApi = clientByCube.get(cube)
      if (skipped || !cubeApi) {
        return { query: modelQuery, skipped: skipped ?? 'cube not in catalog' }
      }
      const query = toCubeQuery(modelQuery, {
        ...scope,
        globalFilters: carriedScope
          ? { [cube]: carriedScope }
          : scope.globalFilters,
        serviceAreaUuid: cubesWithServiceArea.has(cube)
          ? serviceAreaUuid
          : undefined,
      })
      const resultSet = await cubeApi.load(query)
      return { query: modelQuery, rows: resultSet.rawData() }
    }),
  )
  return fitDataResult(results)
}

type DataResultEntry =
  | { query: ModelQuery; skipped: SkipReason }
  | { query: ModelQuery; rows: unknown[]; totalRows?: number }

/**
 * Serialises the results within the backend's size limit. Over it, every
 * query keeps the same share of its rows, so none drops out entirely, and
 * `totalRows` tells the model the rows are cut.
 */
const fitDataResult = (results: DataResultEntry[]): string => {
  const full = JSON.stringify(results)
  const bytes = new TextEncoder().encode(full).length
  if (bytes <= MAX_DATA_RESULT_BYTES) return full

  // Halve the share until it fits. Ten steps reach ~0.1% of the rows.
  let share = MAX_DATA_RESULT_BYTES / bytes
  for (let step = 0; step < 10; step++, share /= 2) {
    const cut = JSON.stringify(
      results.map(entry =>
        'rows' in entry
          ? {
              query: entry.query,
              rows: entry.rows.slice(0, Math.floor(entry.rows.length * share)),
              totalRows: entry.rows.length,
            }
          : entry,
      ),
    )
    if (new TextEncoder().encode(cut).length <= MAX_DATA_RESULT_BYTES) {
      return cut
    }
  }
  return '[]'
}

const hasAnyFilter = (globalFilters: GlobalCubeFilters | undefined) =>
  Array.isArray(globalFilters)
    ? globalFilters.length > 0
    : Object.values(globalFilters ?? {}).some(filters => filters?.length)

// A flat filter list applies to every cube, as in `mergeCubeFilters`.
const hasGlobalFilterFor = (
  globalFilters: GlobalCubeFilters | undefined,
  cube: string,
) =>
  Array.isArray(globalFilters)
    ? globalFilters.length > 0
    : Boolean(globalFilters?.[cube]?.length)

/** The view filters a cube outside the view can take over by name. */
const SCOPE_DIMENSIONS = ['fleet_id', 'region_id']

/**
 * The view's fleet and region filters, moved onto a cube the view doesn't
 * render, so the model queries it in the tiles' scope. Null when the view
 * filters on anything else, on values that differ between its cubes, or on a
 * dimension the cube lacks: the cube would see more than the view.
 */
const carryViewScope = (
  globalFilters: GlobalCubeFilters | undefined,
  cube: string,
  catalogDimensions: Set<string>,
): Filter[] | null => {
  if (!globalFilters || Array.isArray(globalFilters)) return null
  const valuesByDimension = new Map<string, string[]>()
  for (const filter of Object.values(globalFilters).flat()) {
    if (!filter) continue
    if (!('member' in filter) || filter.operator !== 'equals') return null
    if (!('values' in filter) || !filter.values) return null
    const dimension = filter.member?.split('.').pop() ?? ''
    if (!SCOPE_DIMENSIONS.includes(dimension)) return null
    if (!catalogDimensions.has(`${cube}.${dimension}`)) return null
    const known = valuesByDimension.get(dimension)
    if (known && known.join() !== filter.values.join()) return null
    valuesByDimension.set(dimension, filter.values)
  }
  if (valuesByDimension.size === 0) return null
  return [...valuesByDimension].map(([dimension, values]) => ({
    member: `${cube}.${dimension}`,
    operator: 'equals',
    values,
  }))
}

const getCubesWithServiceArea = (meta: CubeMetas): Set<string> =>
  new Set(
    meta.cubes
      .filter(cube =>
        cube.dimensions.some(
          dimension => dimension.name === `${cube.name}.service_area_uuid`,
        ),
      )
      .map(cube => cube.name),
  )

/** Keeps the well-formed queries, so a partly broken request still runs. */
export const parseModelQueries = (queries: unknown): ModelQuery[] => {
  if (!Array.isArray(queries)) return []
  return queries.filter(
    (query): query is ModelQuery =>
      typeof query === 'object' &&
      query !== null &&
      typeof (query as ModelQuery).cube === 'string' &&
      Array.isArray((query as ModelQuery).measures),
  )
}

export type MinervaActivityQuery = { cube: string; measures: string[] }

/** What the activity line lists: each well-formed query's cube and measures. */
export const toMinervaActivityQueries = (
  queries: unknown,
): MinervaActivityQuery[] =>
  parseModelQueries(queries).map(({ cube, measures }) => ({
    cube,
    measures: measures.filter(
      (measure): measure is string => typeof measure === 'string',
    ),
  }))

export const isMinervaDataRequest = (
  result: { __typename?: 'MinervaAnswer' | 'MinervaDataRequest' } | undefined,
): result is MinervaDataRequest => result?.__typename === 'MinervaDataRequest'
